import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@truepill/react-capsule'
import { useMutation, useQuery } from '@truepill/tpos-react-router'
import { PrinterPurpose, LaunchDarkly, OtcProductStatus } from '@truepill/tpos-types'
import { StyledTaskEntry } from 'components/TaskList'
import { Badge } from 'components/UserInitialsBadge'
import { GET_OTC_BY_SKUS, GET_PACKING_MATERIALS_BY_SKUS, SEND_OUTBOUND_SCAN } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useInfoToast from 'hooks/toast/useInfoToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import useAutoPrintShippingLabel from 'hooks/useAutoPrintShippingLabel'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import useBarcodeScanner, { REGEXES } from 'hooks/useBarcodeScanner'
import useDevToolBarcode from 'hooks/useDevtoolBarcode'
import PrintShippingLabelModal from 'modals/PrintShippingLabelModal'
import { useClient, useFlag } from 'providers/LaunchDarklyProvider'
import { usePrintProvider } from 'providers/PrintProvider'
import { useTaskContext } from 'providers/TaskProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import styled from 'styled-components'
import { capsulePrimaryColorDark } from 'styles/styleVariables'
import type { Order } from 'types'
import { isLabelPrinted, generateTasks, shortCodeScan, SKUScan, UPCScan, GS1Scan, shippingLabelScan } from 'utils'
import { formatDate } from 'utils/dates'

type Instruction = {
  instructionText: string
  rightButtonText?: string
  rightButtonHandler?: () => void
  scannedLabel?: string
  printerStatuses?: { purpose: string; selection: string | null; ok: boolean }[]
}

type PackingTasksProps = {
  order: Order
  trackingNumber?: string
  setTrackingNumber: (trackingNumber: string) => void
}

const PackingTasks = ({ order, trackingNumber, setTrackingNumber }: PackingTasksProps): JSX.Element => {
  const { initTasks, completeTask, updateTask, tasks } = useTaskContext()
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [instructions, setInstructions] = useState<Instruction[]>([])
  const initTasksRef = useAutoUpdatingRef(initTasks)
  const tasksRef = useAutoUpdatingRef(tasks)
  const outboundScanInFlightRef = useRef(false)
  const { registerListener, deregisterListener } = useBarcodeScanner()
  const showSuccessToast = useSuccessToast(true)
  const showInfoToast = useInfoToast(true)
  const showErrorToast = useErrorToast(true)
  const hasOtcItems = !!order.otcProducts?.some(otcProduct => otcProduct.otcStatus !== OtcProductStatus.Cancelled)
  const hasPrescriptions = !!order.rxFillRequests?.some(({ fill }) => fill.status === 'Complete')
  const { getSavedPrinterId } = usePrintProvider()
  const { getPrinterById, getLocationNameById } = useTPCacheContext()
  const shippingLabelPrinterId = getSavedPrinterId(PrinterPurpose.ShippingLabel)
  const shippingLabelPrinter = shippingLabelPrinterId ? getPrinterById(shippingLabelPrinterId) : null
  const isOtcAdjudicationOrder = order?.rxFillRequests.every(r => !!r.otcProductId) ?? false
  // If the feature flag shouldOutboundScan is on, then there will be a last packing task requiring outbound scan
  const [shouldOutboundScan, setShouldOutboundScan] = useState(
    useFlag(LaunchDarkly.FeatureFlags.REQUIRE_USER_TO_OUTBOUND_SCAN_DURING_PACKING),
  )
  const [shouldAutoPrintLabel, setShouldAutoPrintLabel] = useState(
    useFlag(LaunchDarkly.FeatureFlags.ENABLE_AUTO_SHIPPING_LABEL_PRINTING),
  )
  const [sendOutboundScan] = useMutation(SEND_OUTBOUND_SCAN, {
    onCompleted: data => {
      outboundScanInFlightRef.current = false
      const outboundScan = data?.sendOutboundScan
      showSuccessToast(
        `${outboundScan?.patientType}: ${outboundScan?.patientName}. Scan at: ${formatDate(
          outboundScan?.scanAt,
          'MM/DD/YYYY hh:mmA',
        )}`,
      )

      // set the scanned tracking number to display at the last instruction
      setInstructions(prevInstructions => {
        const lastInstruction = prevInstructions[prevInstructions.length - 1]
        return [
          ...prevInstructions.slice(0, prevInstructions.length - 1),
          {
            ...lastInstruction,
            scannedLabel: outboundScan?.trackingNumber,
          },
        ]
      })
    },
    onError: error => {
      outboundScanInFlightRef.current = false
      showErrorToast(`Failed to Outbound Scan: ${error?.message}`)
    },
  })

  const acquireOutboundScanLock = useCallback(() => {
    if (outboundScanInFlightRef.current) return false
    outboundScanInFlightRef.current = true
    return true
  }, [])

  // Set the customer id for upcoming feature-flag calls
  const { ldReady, ldIdentify } = useClient()

  const customerLegacyId = order.customer?.legacyId
  const locationLegacyId = order.location?.legacyId

  useEffect(() => {
    if (ldReady && customerLegacyId && locationLegacyId) {
      ldIdentify(
        {
          key: customerLegacyId.toString(),
          custom: { locationLegacyId: locationLegacyId.toString() },
        },
        undefined,
        (err, flags) => {
          if (flags) {
            setShouldOutboundScan(flags[LaunchDarkly.FeatureFlags.REQUIRE_USER_TO_OUTBOUND_SCAN_DURING_PACKING])
            setShouldAutoPrintLabel(flags[LaunchDarkly.FeatureFlags.ENABLE_AUTO_SHIPPING_LABEL_PRINTING])
          }
        },
      )
    }
  }, [ldReady, ldIdentify, customerLegacyId, locationLegacyId])

  const skus = order.otcProducts?.map(otcProduct => otcProduct.sku) || []
  const queryOTCs = order?.otcProducts?.length > 0
  const { data: otcs, loading: loadingOtcs } = useQuery(GET_OTC_BY_SKUS, {
    variables: { customerId: order.customerId, skus },
    skip: !queryOTCs,
  })

  const enablePackingMaterialsScanning = useFlag(LaunchDarkly.FeatureFlags.ENABLE_PACKING_MATERIALS_ACCURACY_SCANNING)
  const packingSkus = order.packingMaterials?.map(packingProduct => packingProduct.sku) || []
  const queryPackingMaterials =
    enablePackingMaterialsScanning && order?.packingMaterials?.length && order?.packingMaterials?.length > 0
  const { data: packingMaterials, loading: loadingPackingMaterials } = useQuery(GET_PACKING_MATERIALS_BY_SKUS, {
    variables: { skus: packingSkus },
    skip: !queryPackingMaterials,
  })

  // Prevent early auto-print before we know all scannable OTC/PM items for this order.
  const packingTasksDataReady = (!queryOTCs || !loadingOtcs) && (!queryPackingMaterials || !loadingPackingMaterials)

  // useLayoutEffect to ensure the tasks are generated before the first render
  useLayoutEffect(() => {
    const ordersTasks = generateTasks({
      rxFillRequests: order.rxFillRequests,
      otcProducts: order.otcProducts,
      otcs: otcs?.getOtcBySKUS,
      packingMaterialItems: order.packingMaterials,
      packingMaterials: packingMaterials?.getPackingMaterialsBySKUS,
      isLabelPrinted: isLabelPrinted(order),
      existingTasks: tasks,
      orderInTriage: !!order.inTriage,
    })
    initTasksRef.current(ordersTasks)

    return () => {
      initTasksRef.current([])
    }
    // Do NOT add missing order and tasks to the dependencies array since that would reset the tasks not allowing the user to complete the order
    // Before adding them we need to fix tasks reset issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order._id, packingMaterials, initTasksRef, otcs, order.rxFillRequests, order.otcProducts, !!order.inTriage])

  useAutoPrintShippingLabel({
    order,
    tasks,
    packingTasksDataReady,
    shouldAutoPrintLabel,
    openPrintModal: useCallback(() => setShowPrintModal(true), []),
    closePrintModal: useCallback(() => setShowPrintModal(false), []),
    setTrackingNumber,
  })

  useDevToolBarcode(
    tasks.map(({ label, code, productType, additionalData }) => ({ label, code, productType, additionalData })),
  )

  const handleShortCodeScan = useCallback(
    (value: string) => {
      shortCodeScan(value, order.rxFillRequests, tasksRef.current, completeTask, showInfoToast, showSuccessToast)
    },
    [tasksRef, completeTask, showInfoToast, showSuccessToast, order.rxFillRequests],
  )

  const handleSKUScan = useCallback(
    (value: string) => {
      SKUScan(value, tasksRef.current, completeTask, showSuccessToast, showErrorToast)
    },
    [tasksRef, completeTask, showSuccessToast, showErrorToast],
  )

  const handleUPCScan = useCallback(
    (value: string) => {
      UPCScan(value, tasksRef.current, updateTask, completeTask, showSuccessToast, showErrorToast)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enablePackingMaterialsScanning, tasksRef, updateTask, completeTask, showSuccessToast, showErrorToast],
  )

  const handleGS1Scan = useCallback(
    (value: string) => {
      GS1Scan(value, tasksRef.current, updateTask, completeTask, showSuccessToast, showErrorToast)
    },
    [tasksRef, updateTask, completeTask, showSuccessToast, showErrorToast],
  )

  const handleShippingLabelScan = useCallback(
    (value: string) => {
      shippingLabelScan(
        value,
        trackingNumber,
        tasksRef.current,
        order.otcProducts,
        order.packingMaterials,
        order.coreOrderId,
        order.status,
        isLabelPrinted(order),
        sendOutboundScan,
        showSuccessToast,
        showErrorToast,
        acquireOutboundScanLock,
      )
    },
    [trackingNumber, tasksRef, order, sendOutboundScan, showSuccessToast, showErrorToast, acquireOutboundScanLock],
  )

  const hasIncompleteTasks = tasks.some(({ completed }) => !completed)

  useEffect(() => {
    registerListener(REGEXES.ShortFillCode, handleShortCodeScan)
    registerListener(REGEXES.UPC, handleUPCScan)
    registerListener(REGEXES.SKU, handleSKUScan)
    if (hasIncompleteTasks) registerListener(REGEXES.GS1, handleGS1Scan)
    if (shouldOutboundScan && !hasIncompleteTasks) registerListener(REGEXES.ShippingLabel, handleShippingLabelScan)
    return () => {
      deregisterListener(REGEXES.ShortFillCode, handleShortCodeScan)
      deregisterListener(REGEXES.UPC, handleUPCScan)
      deregisterListener(REGEXES.GS1, handleGS1Scan)
      deregisterListener(REGEXES.SKU, handleSKUScan)
      deregisterListener(REGEXES.ShippingLabel, handleShippingLabelScan)
    }
  }, [
    enablePackingMaterialsScanning,
    shouldOutboundScan,
    hasIncompleteTasks,
    registerListener,
    handleShortCodeScan,
    handleUPCScan,
    handleSKUScan,
    handleGS1Scan,
    handleShippingLabelScan,
    deregisterListener,
  ])

  useEffect(() => {
    const newInstructions = [
      ...(hasPrescriptions && !isOtcAdjudicationOrder
        ? [
            {
              instructionText: 'Pack prescription items',
            },
          ]
        : []),
      ...(hasOtcItems
        ? [
            {
              instructionText: 'Pack OTC items',
            },
          ]
        : []),
      {
        instructionText: 'Pack any inserts',
      },
      {
        instructionText: 'Print shipping label',
        rightButtonText: 'Print setting',
        rightButtonHandler: () => setShowPrintModal(true),
        printerStatuses: [
          {
            purpose: 'Shipping label',
            selection: shippingLabelPrinter
              ? `${shippingLabelPrinter.printerName} - ${getLocationNameById(shippingLabelPrinter.locationId)}`
              : null,
            ok: !!shippingLabelPrinterId,
          },
        ],
      },
      ...(shouldOutboundScan
        ? [
            {
              instructionText: 'Outbound scan',
              scannedLabel: '',
            },
          ]
        : []),
    ]
    setInstructions(newInstructions)
  }, [
    getLocationNameById,
    hasPrescriptions,
    hasOtcItems,
    isOtcAdjudicationOrder,
    shippingLabelPrinterId,
    shippingLabelPrinter,
    shouldOutboundScan,
  ])

  return (
    <>
      <PrintShippingLabelModal isOpen={showPrintModal} setIsOpen={setShowPrintModal} order={order} />
      <TaskListContainer>
        {instructions.map((instruction, index) => (
          <Fragment key={index}>
            <PresetTask
              lineExtension={instructions[index - 1] ? instructions[index - 1].printerStatuses?.length || 0 : 0}
            >
              <BaseBadge label={`${index + 1}`} />
              <p>{instruction.instructionText}</p>
              {instruction.rightButtonHandler && instruction.rightButtonText && (
                <Button variant="primary-text" onClick={instruction.rightButtonHandler}>
                  {instruction.rightButtonText}
                </Button>
              )}
            </PresetTask>
            {instruction.printerStatuses?.map(({ purpose, selection, ok }) => {
              return (
                <PrinterStatus key={purpose}>
                  <p>
                    {`${purpose}: `}
                    <PrinterStatusSelection ok={ok}>{selection || 'Needs selection'}</PrinterStatusSelection>
                  </p>
                </PrinterStatus>
              )
            })}
            {instruction?.scannedLabel && (
              <PrinterStatus key="scannedLabel">
                <p>{`Label: ${instruction.scannedLabel}`}</p>
              </PrinterStatus>
            )}
          </Fragment>
        ))}
      </TaskListContainer>
    </>
  )
}

export default PackingTasks

const PresetTask = styled(StyledTaskEntry)<{ lineExtension: number }>`
  margin-top: 0.625rem;
  position: relative;
  > span {
    font-size: 0.875rem;
  }
  > p {
    margin-left: 0.5rem;
  }
  > button {
    font-size: 14px;
    text-decoration: underline;
    margin-left: 1rem;
    padding: 0;
  }
  :not(:first-child) {
    :after {
      content: '';
      position: absolute;
      left: 15px;
      bottom: 32px;
      height: calc(0.75em + ${props => (props.lineExtension * 1.5).toString()}rem);
      border: 1px solid ${capsulePrimaryColorDark};
    }
  }
`

const PrinterStatus = styled.div`
  color: #535762;
  font-size: 14px;
  margin-left: calc(32px + 0.5rem);
`
const PrinterStatusSelection = styled.span<{ ok: boolean }>`
  color: ${props => (props.ok ? '#535762' : '#E81A33')};
`

const TaskListContainer = styled.div`
  margin-top: 0.625rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-family: 'Lato', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
`

const BaseBadge = styled(Badge)`
  margin-left: -0.0225rem;
  font-size: 16px;
  border: 2px solid ${capsulePrimaryColorDark};
  color: ${capsulePrimaryColorDark};
  width: 32px;
  background: white;
  height: 32px;
  padding: 0;
`
